import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);

const DIFF_COLORS = ['','#22C55E','#4BC8F5','#F9C74F','#FF6B35','#FF4D00'];
const DIFF_LABELS = ['','Beginner','Basic','Intermediate','Advanced','Expert'];

export default function SubcategoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subcategory, setSubcategory] = useState(null);
  const [category, setCategory] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const { data: sub } = await supabase.from('subcategories').select('*, categories(*)').eq('id', id).single();
    const { data: exs } = await supabase.from('exercises').select('*').eq('subcategory_id', id).eq('active', true).order('id');
    if (sub) { setSubcategory(sub); setCategory(sub.categories); }
    if (exs) setExercises(exs);
    setLoading(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#111009', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #2A2520', borderTopColor: '#FF5500', animation: 'spin 0.8s linear infinite' }}/>
    </div>
  );

  if (!subcategory) return null;

  const color = category?.color || '#FF5500';

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif", background: '#111009', minHeight: '100vh', color: '#F0EBE3', maxWidth: 'var(--app-width, 860px)', margin: '0 auto', paddingBottom: 40 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        .fu { animation: fadeUp 0.25s ease forwards; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
        .ex-card { background: #1C1916; border: 1px solid #2A2520; border-radius: 12px;
          cursor: pointer; transition: all 0.18s; overflow: hidden; position: relative; }
        .ex-card:hover { transform: translateY(-2px); border-color: #3A3530; }
        .ex-card:active { transform: scale(0.97); opacity: 0.85; }
      `}</style>

      <div className="bg-grid"/>

      {/* Header */}
      <div style={{
        padding: '52px 20px 20px',
        background: `linear-gradient(180deg, ${color}15 0%, #111009 100%)`,
        borderBottom: `1px solid ${color}18`,
        position: 'relative', zIndex: 1,
      }}>
        <button onClick={() => navigate(`/category/${category?.id}`)} style={{ background: 'transparent', color: '#7A7268', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: "'Barlow Condensed'", fontWeight: 700, letterSpacing: 1 }}>
          <BackIcon/> {category?.name || 'BACK'}
        </button>

        <div>
          <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 11, fontWeight: 700, letterSpacing: 2, color: color, marginBottom: 4 }}>
            {category?.name?.toUpperCase()}
          </div>
          <h1 style={{ fontFamily: "'Barlow Condensed'", fontSize: 34, fontWeight: 900, letterSpacing: 1, color: '#F0EBE3', lineHeight: 1 }}>
            {subcategory.name}
          </h1>
          <div style={{ fontSize: 12, color: '#7A7268', marginTop: 4 }}>
            {exercises.length} exercises
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 16px', position: 'relative', zIndex: 1 }}>
        <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#7A7268', marginBottom: 14 }}>
          EXERCISES
        </div>

        {exercises.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#3A3530' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🥁</div>
            <div style={{ fontFamily: "'Barlow Condensed'", fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>NO EXERCISES</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Adiciona exercises no painel de admin</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(var(--content-cols, 2), 1fr)', gap: 10 }}>
          {exercises.map((ex, i) => (
            <div key={ex.id} className={`ex-card fu`} style={{ animationDelay: `${i * 0.04}s`, opacity: 0 }}
              onClick={() => navigate(`/exercise/${ex.id}`)}>

              {/* Image */}
              <div style={{ height: 110, overflow: 'hidden', position: 'relative' }}>
                {ex.image_url
                  ? <img src={ex.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 24%' }}/>
                  : <div style={{ width: '100%', height: '100%', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>🥁</div>
                }
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #1C1916 0%, transparent 60%)' }}/>
                {/* Difficulty badge */}
                {ex.difficulty && (
                  <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.7)', borderRadius: 6, padding: '2px 6px' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: DIFF_COLORS[ex.difficulty], fontFamily: "'Barlow Condensed'", letterSpacing: 1 }}>
                      {DIFF_LABELS[ex.difficulty]?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding: '10px 12px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#F0EBE3', lineHeight: 1.3, marginBottom: 4 }}>{ex.title}</div>
                <div style={{ fontSize: 11, color: '#FF5500', fontFamily: "'Barlow Condensed'", fontWeight: 700 }}>♩ {ex.default_bpm} BPM</div>
              </div>

              {/* Bottom color bar */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
